import React, { useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, Box, Stack, Chip,
  List, ListItemButton, ListItemIcon, ListItemText, Typography, CircularProgress,
  useMediaQuery, useTheme,
} from '@mui/material';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import dayjs from 'dayjs';
import { useOpportunities } from '../../hooks/useOpportunities';
import {
  STATUS_LABELS, STATUS_ICONS, getStatusHex, getStatusChipSx,
} from '../../constants/status';
import { formatDate } from '../../utils/format';
import ModalHeader from '../ui/ModalHeader';

// เฉพาะสถานะที่ดีลยังเปิดอยู่ (ยังไม่จบ) เท่านั้นที่ถือว่ามี "งาน" ให้ทำ — ตัด WON/LOST/CANCELLED
// ออกทั้งจากตัวเลือก filter chip และรายการ task เอง (เดิม chip "ทั้งหมด"/สถานะปิดงานเลือกได้
// ทำให้ deal ที่จบแล้วโผล่มาในรายการงานทั้งหมดด้วย) ตรงกับกลุ่มเดียวกับ useOpportunities()'s
// default statusFilter และ OPEN_STATUSES ใน OwnerOpportunitiesTab.js/CheckinPage.js
const OPEN_STATUSES = ['WAITING', 'IN_PROGRESS', 'ON_HOLD'];

// รายการงานทั้งหมดแบบ checklist (คล้าย Google Tasks) — "งาน" ในที่นี้ไม่ใช่โอกาสทางธุรกิจ 1
// รายการ = 1 งาน แต่แตกจากทุกวันที่ผูกกับโอกาสทางธุรกิจแต่ละอัน: วันที่เสนอราคา
// (quotation_due_date) 1 งาน + วันที่นัด (marketing_appointments) อีกอันละ 1 งาน เรียงตามวันที่
// ล่าสุดก่อน (ใหม่ไปเก่า ตามที่ user ขอ) — ใช้ useOpportunities() ตัวเดียวกับตาราง list (คนละ
// instance/state ของตัวเอง ไม่แชร์กับตารางหลัก) เอาแค่ rows + สถานะ + infinite scroll มาแตกเป็น
// task ทีหลัง ไม่ทำ fetch เองใหม่ซ้ำซ้อน — บังคับ sort เป็น quotation_due_date/desc ตอนเปิด (ต่างจาก
// default ของตาราง list ที่เรียงตาม created_at) ให้ลำดับที่โหลดมาใกล้เคียงลำดับวันที่ของ task จริง
// ที่สุดเท่าที่ทำได้ตอน infinite scroll ยังโหลดมาไม่ครบทุกหน้า
export function OpportunityTaskListModal({ open, onClose, onOpportunityClick }) {
  const {
    rows, loading, loadingMore, hasMore, statusFilter, toggleStatusFilter, setStatusFilter,
    setSortBy, setSortOrder, scrollRef, handleScroll,
  } = useOpportunities();

  useEffect(() => {
    setSortBy('quotation_due_date');
    setSortOrder('desc');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tasks = useMemo(() => {
    const items = [];
    rows.filter((row) => OPEN_STATUSES.includes(row.status)).forEach((row) => {
      if (row.quotation_due_date) {
        items.push({
          id: `q-${row.id}`,
          type: 'quotation',
          opportunityId: row.id,
          date: row.quotation_due_date,
          label: 'วันเสนอราคา',
          projectName: row.project_name,
          contactName: row.contact_name_snapshot,
          status: row.status,
        });
      }
      (row.marketing_appointments || []).forEach((appt) => {
        items.push({
          id: `a-${appt.id}`,
          type: 'appointment',
          opportunityId: row.id,
          date: appt.date,
          label: appt.title || 'นัดหมาย',
          projectName: row.project_name,
          contactName: row.contact_name_snapshot,
          status: row.status,
        });
      });
    });
    return items.sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
  }, [rows]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, height: fullScreen ? '100%' : '85vh' } }}
    >
      <ModalHeader
        icon={<ChecklistOutlinedIcon fontSize="small" />}
        title="รายการงานทั้งหมด"
        onClose={onClose}
      />

      <Box sx={{
        px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', overflowX: 'auto',
      }}
      >
        <Stack direction="row" spacing={1}>
          <Chip
            label="ทั้งหมด"
            size="small"
            color={statusFilter.length === OPEN_STATUSES.length ? 'primary' : 'default'}
            variant={statusFilter.length === OPEN_STATUSES.length ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter(OPEN_STATUSES)}
          />
          {OPEN_STATUSES.map((s) => {
            const Icon = STATUS_ICONS[s];
            const active = statusFilter.includes(s);
            return (
              <Chip
                key={s}
                icon={<Icon fontSize="small" />}
                label={STATUS_LABELS[s]}
                size="small"
                variant={active ? 'filled' : 'outlined'}
                onClick={() => toggleStatusFilter(s)}
                sx={getStatusChipSx(s, active ? 'filled' : 'outlined')}
              />
            );
          })}
        </Stack>
      </Box>

      <DialogContent ref={scrollRef} onScroll={handleScroll} sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <InboxOutlinedIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2">ไม่พบรายการงาน</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {tasks.map((task) => {
              const Icon = STATUS_ICONS[task.status];
              return (
                <ListItemButton
                  key={task.id}
                  divider
                  onClick={() => onOpportunityClick?.(task.opportunityId)}
                  sx={{ py: 1.25, alignItems: 'flex-start' }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <Icon fontSize="small" sx={{ color: getStatusHex(task.status) }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={(
                      <Typography variant="body2" fontWeight={600}>
                        {task.label}
                        {' · '}
                        {formatDate(task.date)}
                      </Typography>
                    )}
                    secondary={(
                      <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                        {task.projectName}
                        {task.contactName ? ` — ${task.contactName}` : ''}
                      </Typography>
                    )}
                  />
                  <Chip
                    label={STATUS_LABELS[task.status]}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1, mt: 0.5, ...getStatusChipSx(task.status, 'outlined') }}
                  />
                </ListItemButton>
              );
            })}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!hasMore && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1.5 }}>
                — สิ้นสุดรายการ ({tasks.length} งาน) —
              </Typography>
            )}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default OpportunityTaskListModal;
